/**
 * Error boundary component
 * Catches React errors and displays a fallback UI with navigation options
 */

'use client';

import { Component, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertTriangle, ArrowLeft, Home } from 'lucide-react';
import Link from 'next/link';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

// Wrapper component to use hooks
function ErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  const router = useRouter();

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="p-8 max-w-md w-full">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
        <h1 className="text-2xl font-bold mb-2 text-center">Something went wrong</h1>
        <p className="text-muted-foreground mb-4 text-center">
          We encountered an unexpected error. You can try again or go back to the homepage.
        </p>
        {process.env.NODE_ENV === 'development' && error && (
          <pre className="text-xs bg-muted p-4 rounded mb-4 overflow-auto max-h-32">
            {error.message}
          </pre>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              onClick={() => {
                onReset();
                router.back();
              }}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Link href="/" className="flex-1">
              <Button variant="outline" className="w-full">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </Link>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                onReset();
                window.location.reload();
              }}
              className="flex-1"
            >
              Reload Page
            </Button>
            <Button variant="outline" onClick={onReset} className="flex-1">
              Try Again
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Don't show error UI for Supabase configuration errors
    if (error.message.includes('Supabase URL and Anon Key must be set')) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Silently handle Supabase configuration errors - these are expected when Supabase isn't configured
    if (error.message.includes('Supabase URL and Anon Key must be set')) {
      // This is expected when Supabase isn't configured - don't log as error
      if (process.env.NODE_ENV === 'development') {
        console.warn('Supabase not configured (this is normal for guest mode):', error.message);
      }
      // Reset the error state to allow the app to continue
      this.setState({ hasError: false, error: null });
      return;
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // TODO: Send to Sentry
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={() => {
            this.setState({ hasError: false, error: null });
          }}
        />
      );
    }

    return this.props.children;
  }
}
